import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";

function toArray(children) {
  return React.Children.toArray(children).filter(Boolean);
}

function mergeProps(baseProps = {}, overrideProps = {}) {
  const {
    className: baseClassName,
    style: baseStyle,
    ...baseOther
  } = baseProps;
  const {
    className: overrideClassName,
    style: overrideStyle,
    ...overrideOther
  } = overrideProps;
  const className = [baseClassName, overrideClassName]
    .filter(Boolean)
    .join(" ");
  const style = {
    ...(baseStyle || {}),
    ...(overrideStyle || {}),
  };
  const mergedProps = {
    ...baseOther,
    ...overrideOther,
  };

  if (className) mergedProps.className = className;
  if (Object.keys(style).length) mergedProps.style = style;

  return mergedProps;
}

function getIndicatorIcon(IndicatorIcon, itemIndex) {
  if (Array.isArray(IndicatorIcon)) {
    return IndicatorIcon[itemIndex % IndicatorIcon.length] || null;
  }

  return IndicatorIcon || <FiberManualRecordIcon />;
}

export default function Carousel({
  children,
  index,
  startAt,
  onChange,
  autoPlay = true,
  interval = 4000,
  indicators = true,
  indicatorContainerProps = {},
  indicatorIconButtonProps = {},
  activeIndicatorIconButtonProps = {},
  IndicatorIcon,
  className,
  classes = {},
  sx,
  height,
  animation,
  duration,
  navButtonsAlwaysInvisible,
  swipe,
  ...other
}) {
  void animation;
  void duration;
  void navButtonsAlwaysInvisible;
  void swipe;

  const items = useMemo(() => toArray(children), [children]);
  const controlledIndex = index ?? startAt;
  const [active, setActive] = useState(controlledIndex || 0);

  useEffect(() => {
    if (controlledIndex !== undefined) {
      setActive(controlledIndex);
    }
  }, [controlledIndex]);

  useEffect(() => {
    if (!autoPlay || items.length < 2) return undefined;

    const timer = window.setInterval(() => {
      setActive((current) => {
        const next = (current + 1) % items.length;
        onChange?.(next, current);
        return next;
      });
    }, interval);

    return () => window.clearInterval(timer);
  }, [autoPlay, interval, items.length, onChange]);

  const selectItem = (next) => {
    const previous = active;
    setActive(next);
    onChange?.(next, previous);
  };
  const safeActive = items.length ? Math.min(active, items.length - 1) : 0;

  return (
    <Box
      className={
        [classes.root, className].filter(Boolean).join(" ") || undefined
      }
      sx={{ height, ...sx }}
      {...other}
    >
      {items[safeActive] || null}
      {indicators && items.length > 1 && (
        <Box {...indicatorContainerProps}>
          {items.map((_, itemIndex) => {
            const activeProps =
              itemIndex === safeActive ? activeIndicatorIconButtonProps : {};
            const iconButtonProps = mergeProps(
              indicatorIconButtonProps,
              activeProps,
            );

            return (
              <IconButton
                key={itemIndex}
                type="button"
                size="small"
                onClick={() => selectItem(itemIndex)}
                {...iconButtonProps}
              >
                {getIndicatorIcon(IndicatorIcon, itemIndex)}
              </IconButton>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

Carousel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number,
  startAt: PropTypes.number,
  onChange: PropTypes.func,
  autoPlay: PropTypes.bool,
  interval: PropTypes.number,
  indicators: PropTypes.bool,
  indicatorContainerProps: PropTypes.object,
  indicatorIconButtonProps: PropTypes.object,
  activeIndicatorIconButtonProps: PropTypes.object,
  IndicatorIcon: PropTypes.node,
  animation: PropTypes.string,
  className: PropTypes.string,
  classes: PropTypes.object,
  duration: PropTypes.number,
  sx: PropTypes.object,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  navButtonsAlwaysInvisible: PropTypes.bool,
  swipe: PropTypes.bool,
};
